import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle, CreditCard, DollarSign, X } from 'lucide-react';
import { quickSale, type CheckoutPaymentInput } from '@/lib/api/payments.js';
import { broadcastToDisplay } from '@/lib/api/display-staff.js';
import type { StoreSettings } from '@/lib/api/stores.js';
import {
  cartLineLabel,
  computeCartTotals,
  itemLineTotal,
} from '@/lib/pos/cart-math.js';
import { amountDueForMethod, calculateCashDiscount } from '@/lib/cash-discount.js';
import { formatCurrency } from '@/lib/format.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';
import {
  ManualCardPaymentForm,
  isManualCardValid,
  type ManualCardDetails,
} from '@/components/payments/ManualCardPaymentForm.js';
import { PrintButton } from '@/components/printing/PrintButton.js';
import { usePrintNodeConfigured } from '@/hooks/usePrintNodeConfigured.js';
import { usePosCartStore } from '@/stores/pos-cart.js';

type PosCheckoutModalProps = {
  open: boolean;
  store: StoreSettings;
  onClose: () => void;
  onComplete: () => void;
};

type Method = 'cash' | 'card' | 'split';

export function PosCheckoutModal({ open, store, onClose, onComplete }: PosCheckoutModalProps) {
  const queryClient = useQueryClient();
  const items = usePosCartStore((s) => s.items);
  const customer = usePosCartStore((s) => s.customer);
  const cartDiscount = usePosCartStore((s) => s.cartDiscount);
  const loyaltyApplied = usePosCartStore((s) => s.loyaltyApplied);
  const storeCreditApplied = usePosCartStore((s) => s.storeCreditApplied);
  const managerOverrideAuthorizedBy = usePosCartStore((s) => s.managerOverrideAuthorizedBy);
  const clearCart = usePosCartStore((s) => s.clearCart);

  const [method, setMethod] = useState<Method>('cash');
  const [cashTendered, setCashTendered] = useState('');
  const [splitCash, setSplitCash] = useState('');
  const [tipAmount, setTipAmount] = useState('');
  const [tipPin, setTipPin] = useState('');
  const [cardDetails, setCardDetails] = useState<ManualCardDetails>({
    amount: 0,
    transactionId: '',
    cardType: '',
    lastFour: '',
  });
  const [receipt, setReceipt] = useState<{
    ticketNumber: string;
    ticketId: string;
    total: number;
    tip: number;
    changeDue: number;
  } | null>(null);

  const { hasPrintNode: hasReceiptPrinter } = usePrintNodeConfigured('receipt');

  const taxRate = Number(store.taxRate ?? 0);
  const taxMode = store.taxMode ?? 'parts_only';
  const totals = computeCartTotals(items, cartDiscount, taxRate, taxMode);
  const baseDue = Math.max(0, totals.total - loyaltyApplied - storeCreditApplied);

  const cashDiscount = useMemo(
    () =>
      calculateCashDiscount(baseDue, {
        cashDiscountEnabled: store.cashDiscountEnabled ?? null,
        cashDiscountRate: store.cashDiscountRate ?? null,
        cashDiscountMode: store.cashDiscountMode ?? null,
        cashDiscountLabel: store.cashDiscountLabel ?? null,
      }),
    [baseDue, store],
  );

  const cashDue = amountDueForMethod(cashDiscount, 'cash');
  const cardDue = amountDueForMethod(cashDiscount, 'card');
  const terminalAddsSurcharge =
    store.terminalAppliesSurcharge !== false &&
    method === 'card' &&
    cashDiscount.mode === 'card_surcharge' &&
    cashDiscount.discountAmount > 0;
  const chargedSale = method === 'card' ? cardDue : method === 'cash' ? cashDue : baseDue;
  const amountDue = terminalAddsSurcharge ? cashDue : chargedSale;
  const cashSavings =
    cashDiscount.discountAmount > 0 && cashDiscount.cashTotal < cashDiscount.cardTotal
      ? Math.round((cashDiscount.cardTotal - cashDiscount.cashTotal) * 100) / 100
      : 0;

  const tipNum = Math.max(0, Number(tipAmount) || 0);
  const tipsEnabled = Boolean(store.tipsEnabled);
  const tipsOnCard = store.tipsOnCard !== false;
  const tipsOnCash = store.tipsOnCash === true;
  const tipsAllowedForMethod =
    tipsEnabled &&
    ((method === 'card' && tipsOnCard) ||
      (method === 'cash' && tipsOnCash) ||
      (method === 'split' && (tipsOnCard || tipsOnCash)));
  const tipForPayment = tipsAllowedForMethod ? tipNum : 0;
  const totalCharged = Math.round((chargedSale + tipForPayment) * 100) / 100;

  // Cash collected must cover sale amount + tip (when tip is on cash).
  const tipOnCash =
    tipForPayment > 0 &&
    ((method === 'cash' && tipsOnCash) || (method === 'split' && tipsOnCash && !tipsOnCard));
  const cashCollectWithTip =
    method === 'cash' ? cashDue + (tipOnCash ? tipForPayment : 0) : 0;
  const minCashTendered =
    method === 'cash'
      ? cashCollectWithTip
      : method === 'split'
        ? Math.min(Math.max(Number(splitCash || 0), 0), baseDue) + (tipOnCash ? tipForPayment : 0)
        : 0;

  const cashTenderedValue = Number(cashTendered || (method === 'cash' ? cashCollectWithTip : 0));
  const splitCashValue = Math.min(Math.max(Number(splitCash || 0), 0), baseDue);
  const splitCardValue = Math.max(baseDue - splitCashValue, 0);
  const changeDue =
    method === 'cash'
      ? Math.max(cashTenderedValue - cashDue - (tipOnCash ? tipForPayment : 0), 0)
      : 0;
  const cashUnderTendered =
    method === 'cash' && cashDue > 0 && cashTenderedValue < cashCollectWithTip;

  const payments = useMemo((): CheckoutPaymentInput[] => {
    if (baseDue <= 0) return [];
    const tipFields = (attach: boolean): Pick<CheckoutPaymentInput, 'tipAmount' | 'tipPin'> => {
      if (!attach || tipForPayment <= 0) return {};
      return tipPin.trim()
        ? { tipAmount: tipForPayment, tipPin: tipPin.trim() }
        : { tipAmount: tipForPayment };
    };

    if (method === 'cash') {
      const row: CheckoutPaymentInput = {
        method: 'cash',
        amount: cashDue,
        cashTendered: cashTenderedValue,
        ...tipFields(tipsOnCash),
      };
      return [row];
    }
    if (method === 'card') {
      const row: CheckoutPaymentInput = {
        method: 'card',
        amount: cardDetails.amount || cardDue,
        ...tipFields(tipsOnCard),
      };
      if (cardDetails.transactionId.trim()) row.transactionId = cardDetails.transactionId.trim();
      if (cardDetails.cardType) row.cardType = cardDetails.cardType;
      if (cardDetails.lastFour.trim()) row.lastFour = cardDetails.lastFour.trim();
      return [row];
    }

    // Split: attach tip once (card preferred if allowed).
    const putTipOnCard = tipForPayment > 0 && tipsOnCard;
    const putTipOnCashOnly = tipForPayment > 0 && !tipsOnCard && tipsOnCash;
    const out: CheckoutPaymentInput[] = [];
    if (splitCashValue > 0) {
      out.push({
        method: 'cash',
        amount: splitCashValue,
        cashTendered: splitCashValue + (putTipOnCashOnly ? tipForPayment : 0),
        ...tipFields(putTipOnCashOnly),
      });
    }
    if (splitCardValue > 0) {
      const cardRow: CheckoutPaymentInput = {
        method: 'card',
        amount: splitCardValue,
        ...tipFields(putTipOnCard),
      };
      if (cardDetails.transactionId.trim()) cardRow.transactionId = cardDetails.transactionId.trim();
      if (cardDetails.cardType) cardRow.cardType = cardDetails.cardType;
      if (cardDetails.lastFour.trim()) cardRow.lastFour = cardDetails.lastFour.trim();
      out.push(cardRow);
    }
    return out;
  }, [
    baseDue,
    cardDetails,
    cardDue,
    cashDue,
    cashTenderedValue,
    method,
    splitCardValue,
    splitCashValue,
    tipForPayment,
    tipPin,
    tipsOnCard,
    tipsOnCash,
  ]);

  const saleMutation = useMutation({
    mutationFn: async () => {
      if (cashUnderTendered) {
        throw new Error(
          `Cash tendered must be at least ${formatCurrency(cashCollectWithTip)}${tipOnCash ? ' (includes tip)' : ''}`,
        );
      }
      if (method === 'card' && !isManualCardValid(cardDetails, cardDue)) {
        throw new Error('Enter a valid card amount that does not exceed the balance due');
      }
      if (method === 'split' && payments.length === 0 && baseDue > 0) {
        throw new Error('Enter a cash amount or use card for the full balance');
      }
      if (tipForPayment > 0 && store.tipsRequirePin !== false && !tipPin.trim()) {
        throw new Error('Enter your staff PIN to authorize the tip');
      }

      const storeName = store.name ?? 'Store';
      const qsCustName = customer
        ? `${customer.firstName} ${customer.lastName ?? ''}`.trim()
        : 'Walk-in';

      const lineItemPayloads = items.map((item) => ({
        description: cartLineLabel(item),
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        originalPrice: item.originalPrice,
        type: item.type === 'inventory' ? ('retail' as const) : ('custom' as const),
        ...(item.taxable !== undefined ? { taxable: item.taxable } : {}),
        ...(item.discountAmount ? { discountAmount: item.discountAmount } : {}),
        ...(item.discountPct ? { discountPct: item.discountPct } : {}),
        ...(item.discountReason ? { discountReason: item.discountReason } : {}),
        ...(item.discountPresetId ? { discountPresetId: item.discountPresetId } : {}),
        ...(item.inventoryItemId ? { inventoryItemId: item.inventoryItemId } : {}),
      }));

      await broadcastToDisplay(store.id, {
        type: 'display.checkout',
        payload: {
          mode: 'checkout',
          storeName,
          logoUrl: store.logoUrl ?? null,
          customerName: qsCustName,
          lines: lineItemPayloads.map((l) => ({
            description: l.description,
            quantity: l.quantity,
            unitPrice: l.unitPrice,
            lineTotal: String(l.unitPrice * l.quantity),
          })),
          subtotal: String(totals.subtotal),
          discountTotal: String(totals.youSaved),
          taxTotal: String(totals.tax),
          total: String(chargedSale),
          tipAmount: tipForPayment > 0 ? String(tipForPayment) : undefined,
          storeCreditApplied: String(storeCreditApplied),
          loyaltyCreditApplied: String(loyaltyApplied),
          amountDue: String(totalCharged),
        },
        timestamp: new Date().toISOString(),
      });

      return quickSale({
        items: lineItemPayloads,
        ...(payments.length > 0 ? { payments } : {}),
        ...(totals.cartDiscountValue > 0
          ? {
              cartDiscountAmount: totals.cartDiscountValue,
              cartDiscountReason: cartDiscount?.reason ?? 'Cart discount',
              ...(cartDiscount?.discountPresetId
                ? { cartDiscountPresetId: cartDiscount.discountPresetId }
                : {}),
            }
          : {}),
        ...(customer?.id ? { customerId: customer.id } : {}),
        ...(storeCreditApplied > 0 ? { storeCreditApplied } : {}),
        ...(loyaltyApplied > 0 ? { loyaltyPointsRedeemed: Math.round(loyaltyApplied * 100) } : {}),
        ...(managerOverrideAuthorizedBy ? { managerOverrideAuthorizedBy } : {}),
      });
    },
    onSuccess: (result) => {
      setReceipt({
        ticketNumber: result.ticket.ticketNumber,
        ticketId: result.ticket.id,
        total: Number(result.receipt.total),
        tip: tipForPayment,
        changeDue: Number(result.changeDue ?? changeDue),
      });
      toastSuccess(`Sale ${result.ticket.ticketNumber} complete`);
      if (result.cashSessionWarning) toastError(result.cashSessionWarning);
      queryClient.invalidateQueries({ queryKey: ['cash-session-current'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-items'] });
      queryClient.invalidateQueries({ queryKey: ['pos-accessories'] });
      queryClient.invalidateQueries({ queryKey: ['pos-recent-items'] });
      queryClient.invalidateQueries({ queryKey: ['pos-misc'] });
      queryClient.invalidateQueries({ queryKey: ['pos-pinned'] });
      queryClient.invalidateQueries({ queryKey: ['pos-scan'] });
    },
    onError: (error) => {
      const parsed = parseApiError(error);
      toastError(parsed.message, parsed.code);
    },
  });

  function selectMethod(next: Method) {
    setMethod(next);
    setTipAmount('');
    setTipPin('');
    if (next === 'card') {
      setCardDetails((prev) => ({ ...prev, amount: cardDue }));
    }
  }

  if (!open) return null;

  if (receipt) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4">
        <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
          <div className="flex items-center gap-3">
            <CheckCircle className="h-8 w-8 text-green-600" />
            <div>
              <h2 className="text-xl font-bold">Sale Complete</h2>
              <p className="text-sm font-semibold text-primary">{receipt.ticketNumber}</p>
            </div>
          </div>
          <div className="mt-4 space-y-1 text-sm">
            {items.map((item) => (
              <div key={item.id} className="flex justify-between gap-2">
                <span>{cartLineLabel(item)}</span>
                <span className="font-semibold">{formatCurrency(itemLineTotal(item))}</span>
              </div>
            ))}
            <div className="flex justify-between border-t border-slate-200 pt-2 font-bold">
              <span>Total</span>
              <span>{formatCurrency(receipt.total)}</span>
            </div>
            {receipt.tip > 0 ? (
              <div className="flex justify-between text-slate-700">
                <span>Tip</span>
                <span className="font-semibold">{formatCurrency(receipt.tip)}</span>
              </div>
            ) : null}
            {method === 'cash' || (method === 'split' && receipt.changeDue > 0) ? (
              <div className="flex justify-between text-green-700">
                <span>Change</span>
                <span>{formatCurrency(receipt.changeDue)}</span>
              </div>
            ) : null}
          </div>
          <div className="mt-5 grid gap-2 sm:grid-cols-2">
            <PrintButton
              type="receipt"
              ticketId={receipt.ticketId}
              hasPrintNode={hasReceiptPrinter}
              className="w-full justify-center"
            />
            <button
              type="button"
              onClick={() => {
                clearCart();
                setReceipt(null);
                setTipAmount('');
                setTipPin('');
                onComplete();
                onClose();
              }}
              className="sm:col-span-2 rounded-xl bg-primary py-2 text-sm font-bold text-white"
            >
              New Sale
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4">
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <h2 className="text-lg font-bold text-slate-950">Checkout</h2>
          <button type="button" onClick={onClose} className="text-slate-500">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="max-h-[60vh] space-y-2 overflow-y-auto px-5 py-4">
          {items.map((item) => (
            <div key={item.id} className="flex justify-between text-sm">
              <span>
                {cartLineLabel(item)} × {item.quantity}
              </span>
              <span className="font-semibold">{formatCurrency(itemLineTotal(item))}</span>
            </div>
          ))}
        </div>
        <div className="space-y-3 border-t border-slate-200 px-5 py-4">
          <div className="flex justify-between text-lg font-bold">
            <span>Amount due</span>
            <span>{formatCurrency(amountDue)}</span>
          </div>
          {terminalAddsSurcharge ? (
            <div className="rounded-xl bg-slate-50 px-3 py-2 text-sm">
              <div className="flex justify-between font-semibold">
                <span>Amount charged</span>
                <span>{formatCurrency(chargedSale)}</span>
              </div>
              <p className="mt-1 text-xs text-slate-500">
                Enter the amount due into the card reader. The reader adds the{' '}
                {(cashDiscount.discountRate * 100).toFixed(2)}% surcharge.
              </p>
            </div>
          ) : null}
          <div className="grid grid-cols-3 gap-2">
            {(['cash', 'card', 'split'] as const).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => selectMethod(m)}
                className={[
                  'flex items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-bold capitalize',
                  method === m ? 'bg-primary text-white' : 'bg-slate-100 text-slate-700',
                ].join(' ')}
              >
                {m === 'card' ? <CreditCard className="h-4 w-4" /> : null}
                {m === 'cash' ? <DollarSign className="h-4 w-4" /> : null}
                {m}
              </button>
            ))}
          </div>

          {method === 'cash' && cashSavings > 0 ? (
            <p className="rounded-lg bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-800">
              Customer saves {formatCurrency(cashSavings)} by paying cash!
            </p>
          ) : null}

          {method === 'cash' ? (
            <div className="space-y-3">
              {cashDiscount.discountAmount > 0 ? (
                <div className="space-y-1 rounded-xl bg-slate-50 p-3 text-sm">
                  <div className="flex justify-between">
                    <span>Subtotal</span>
                    <span>{formatCurrency(cashDiscount.originalTotal)}</span>
                  </div>
                  <div className="flex justify-between text-emerald-700">
                    <span>
                      {cashDiscount.discountLabel} ({(cashDiscount.discountRate * 100).toFixed(2)}%)
                    </span>
                    <span>-{formatCurrency(cashDiscount.discountAmount)}</span>
                  </div>
                  <div className="flex justify-between border-t border-slate-200 pt-2 font-bold">
                    <span>Cash total</span>
                    <span>{formatCurrency(cashDue)}</span>
                  </div>
                </div>
              ) : null}
              <label className="block">
                <span className="text-sm font-medium text-slate-700">Amount tendered</span>
                <input
                  className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
                  type="number"
                  min={minCashTendered}
                  step="0.01"
                  placeholder="Cash tendered"
                  value={cashTendered || String(cashCollectWithTip || '')}
                  onChange={(e) => setCashTendered(e.target.value)}
                />
              </label>
              {cashUnderTendered ? (
                <div className="rounded-xl bg-amber-50 p-3 text-center text-sm font-semibold text-amber-800">
                  Tender at least {formatCurrency(cashCollectWithTip)}
                  {tipOnCash ? ' (includes tip)' : ''}
                </div>
              ) : (
                <div className="rounded-xl bg-green-50 p-3 text-center font-bold text-green-700">
                  Change: {formatCurrency(changeDue)}
                </div>
              )}
            </div>
          ) : null}

          {method === 'card' ? (
            <ManualCardPaymentForm
              invoiceTotal={terminalAddsSurcharge ? cashDue : cardDue}
              maxAmount={cardDue}
              value={cardDetails.amount > 0 ? cardDetails : { ...cardDetails, amount: cardDue }}
              onChange={setCardDetails}
              {...(terminalAddsSurcharge ? { readerAmount: cashDue } : {})}
              {...(terminalAddsSurcharge
                ? {}
                : cashDiscount.discountAmount > 0
                  ? {
                      helperText:
                        cashDiscount.mode === 'card_surcharge'
                          ? `Includes card surcharge (${(cashDiscount.discountRate * 100).toFixed(2)}%)`
                          : 'No cash discount for card payments',
                    }
                  : {})}
            />
          ) : null}

          {method === 'split' ? (
            <div className="space-y-3">
              <p className="text-xs text-slate-500">
                Split tenders use the base ticket total (cash discount does not apply).
              </p>
              <label className="block">
                <span className="text-sm font-medium text-slate-700">Cash amount</span>
                <input
                  className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
                  type="number"
                  min="0"
                  max={baseDue}
                  step="0.01"
                  value={splitCash}
                  onChange={(e) => setSplitCash(e.target.value)}
                />
              </label>
              <div className="rounded-xl bg-slate-50 p-3 text-sm text-slate-700">
                Remaining on card: <strong>{formatCurrency(splitCardValue)}</strong>
              </div>
              {splitCardValue > 0 ? (
                <ManualCardPaymentForm
                  invoiceTotal={splitCardValue}
                  maxAmount={splitCardValue}
                  value={{ ...cardDetails, amount: splitCardValue }}
                  onChange={(next) => setCardDetails({ ...next, amount: splitCardValue })}
                />
              ) : null}
            </div>
          ) : null}

          {tipsAllowedForMethod ? (
            <div className="space-y-2 rounded-xl border border-slate-200 p-4">
              <div className="flex justify-between text-sm">
                <span>{terminalAddsSurcharge ? 'Amount due' : 'Sale amount'}</span>
                <span className="font-semibold">{formatCurrency(amountDue)}</span>
              </div>
              {terminalAddsSurcharge ? (
                <div className="flex justify-between text-sm">
                  <span>
                    Card surcharge ({(cashDiscount.discountRate * 100).toFixed(2)}%)
                  </span>
                  <span className="font-semibold">
                    {formatCurrency(cashDiscount.discountAmount)}
                  </span>
                </div>
              ) : null}
              <label className="block text-sm">
                <span className="font-medium text-slate-700">
                  {method === 'card' || (method === 'split' && tipsOnCard)
                    ? 'Tip (enter what the terminal reported)'
                    : 'Tip'}
                </span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={tipAmount}
                  onChange={(e) => setTipAmount(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
                  placeholder="0.00"
                />
              </label>
              {tipForPayment > 0 && store.tipsRequirePin !== false ? (
                <label className="block text-sm">
                  <span className="font-medium text-slate-700">Your PIN (binds tip to you)</span>
                  <input
                    type="password"
                    inputMode="numeric"
                    autoComplete="off"
                    value={tipPin}
                    onChange={(e) => setTipPin(e.target.value)}
                    className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
                    placeholder="••••"
                  />
                </label>
              ) : null}
              <div className="flex justify-between border-t border-slate-200 pt-2 text-base font-bold">
                <span>Total charged</span>
                <span>{formatCurrency(totalCharged)}</span>
              </div>
              <p className="text-xs text-slate-500">
                Tip does not change the sale total and is not taxable.
              </p>
            </div>
          ) : null}

          <button
            type="button"
            disabled={
              saleMutation.isPending ||
              cashUnderTendered ||
              (baseDue > 0 && payments.length === 0) ||
              (method === 'card' &&
                !isManualCardValid(
                  cardDetails.amount > 0 ? cardDetails : { ...cardDetails, amount: cardDue },
                  cardDue,
                )) ||
              (tipForPayment > 0 && store.tipsRequirePin !== false && !tipPin.trim())
            }
            onClick={() => {
              if (method === 'card' && cardDetails.amount <= 0) {
                setCardDetails((prev) => ({ ...prev, amount: cardDue }));
              }
              saleMutation.mutate();
            }}
            className="w-full rounded-xl bg-green-600 py-3 font-bold text-white disabled:opacity-50"
          >
            {saleMutation.isPending
              ? 'Processing…'
              : method === 'card'
                ? terminalAddsSurcharge
                  ? `Process card — enter ${formatCurrency(amountDue)}`
                  : 'Process Card Payment'
                : `Complete — ${formatCurrency(totalCharged)}`}
          </button>
        </div>
      </div>
    </div>
  );
}
